// src/pages/Assets.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import { fmtDay } from '../utils/format';

// ----- format helpers
const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });
const INT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
function fmtGBP(n){ const v = Number(n ?? 0); return GBP.format(Number.isFinite(v) ? v : 0); }
function fmtInt(n){ const v = Number(n ?? 0); return Number.isFinite(v) ? INT.format(v) : '0'; }

// Generate an id when adding a new asset
function genId(){
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `asset-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
}

// Free-text + suggestions input (combobox-ish)
function ComboInput({ id, label, value, onChange, options = [], placeholder = 'Type or pick' }) {
  const listId = `${id}-datalist`;
  return (
    <label className="input-label">
      {label}
      <input
        className="input"
        list={listId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <datalist id={listId}>
        {options.map(opt => <option key={opt} value={opt} />)}
      </datalist>
    </label>
  );
}

const EMPTY_ASSET = {
  item: '',
  make: '',
  model: '',
  serial: '',
  supplier: '',
  location: '',
  purchaseDate: '',
  purchasePrice: '',
  category: '',
  status: '',
  quantity: 1,
};

export default function Assets() {
  const [openActionsFor, setOpenActionsFor] = useState(null);
const [editingId, setEditingId] = useState(null);
const [editing, setEditing] = useState(null); // asset being edited (or null)

  const cmd = useCommands();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters
  const [q, setQ] = useState('');
  const [supplier, setSupplier] = useState('all');
  const [location, setLocation] = useState('all');

  // paging (used in detail mode)
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // move dialog
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveFor, setMoveFor] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [dest, setDest] = useState('');
  const [moveQty, setMoveQty] = useState(1);

  // add form
  const [adding, setAdding] = useState(false);
  const [newAsset, setNewAsset] = useState({
    item: '', make: '', model: '',
    serial: '', supplier: '',
    location: '', purchaseDate: '', purchasePrice: '',
    category: '', status: '', quantity: 1,
  });

  // group view
  const [groupMode, setGroupMode] = useState(false);
  const GROUP_FIELDS = [
    { key:'location', label:'Location' },
    { key:'item',     label:'Item' },
    { key:'category', label:'Category' },
    { key:'supplier', label:'Supplier' },
    { key:'make',     label:'Make' },
    { key:'model',    label:'Model' },
  ];
  const [groupA, setGroupA] = useState('location');
  const [groupB, setGroupB] = useState('item');

  // Load teams once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setTeamsLoading(true);
        const list = await cmd.teams.list().catch(() => []);
        const names = (Array.isArray(list) ? list : [])
          .map(x => typeof x === 'string' ? x : (x?.name || x?.id || ''))
          .filter(Boolean);
        if (alive) setTeams(Array.from(new Set(names)));
      } finally {
        setTeamsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cmd]);

  // Suggestions: teams + known asset locations
  const locationSuggestions = useMemo(() => {
    const locs = rows.map(r => r.location || '').filter(Boolean);
    return Array.from(new Set([...teams, ...locs])).sort((a,b)=>a.localeCompare(b));
  }, [teams, rows]);

  // Load assets
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const data = await cmd.assets.list();
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        const normalized = list.map(a => ({
          id: a.id,
          item: a.item ?? '',
          make: a.make ?? '',
          model: a.model ?? '',
          serial: a.serial ?? '',
          supplier: a.supplier ?? '',
          location: a.location ?? '—',
          purchaseDate: a.purchaseDate ?? '',
          purchasePrice: Number(a.purchasePrice ?? 0),
          category: a.category ?? '',
          status: a.status ?? '—',
          quantity: Number(a.quantity ?? 1),
        }));
        setRows(normalized);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e?.message || 'Failed to load assets');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cmd]);

  // Filters
  const suppliers = useMemo(() => {
    const u = new Set(rows.map(r => r.supplier || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const locations = useMemo(() => {
    const u = new Set(rows.map(r => r.location || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter(r => {
      if (supplier !== 'all' && (r.supplier || '—') !== supplier) return false;
      if (location !== 'all' && (r.location || '—') !== location) return false;
      if (!ql) return true;
      const hay = [
        r.item, r.make, r.model, r.serial,
        r.supplier, r.location, r.category,
        r.status, r.purchaseDate, String(r.purchasePrice)
      ].join(' ').toLowerCase();
      return hay.includes(ql);
    }).sort((a,b)=> (a.item||'').localeCompare(b.item||''));
  }, [rows, q, supplier, location]);

  useEffect(()=>{ setPage(1); }, [q, supplier, location]);

  // Grouping (on filtered data)
  const grouped = useMemo(() => {
    if (!groupMode) return { rows: [], grandQty: 0, grandValue: 0 };
    const kA = groupA, kB = groupB;
    const map = new Map();
    let grandQty = 0, grandValue = 0;

    for (const r of filtered) {
      const a = (r[kA] ?? '—') || '—';
      const b = (r[kB] ?? '—') || '—';
      const key = `${a}||${b}`;
      const prev = map.get(key) || { a, b, qty:0, value:0 };
      prev.qty += Number(r.quantity || 0);
      prev.value += Number(r.purchasePrice || 0) * Number(r.quantity || 1);
      map.set(key, prev);
      grandQty += Number(r.quantity || 0);
      grandValue += Number(r.purchasePrice || 0) * Number(r.quantity || 1);
    }
    const rows = Array.from(map.values()).sort((x,y) => {
      if (x.a === y.a) return String(x.b).localeCompare(String(y.b));
      return String(x.a).localeCompare(String(y.a));
    });
    return { rows, grandQty, grandValue };
  }, [filtered, groupMode, groupA, groupB]);

  // Paging (detail mode only)
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page-1)*pageSize, page*pageSize);

  // Move dialog open
  function openMove(row){
  setMoveFor(row);
  setMoveQty(Number(row.quantity || 1));
  setMoveOpen(true);
  setDest('');
}

  async function confirmMove(){
  if (!moveFor || !dest?.trim()) return;

  const destination = dest.trim();
  const prev = moveFor.location || '—';
  const qtyToMove = Math.max(1, Math.min(moveQty, moveFor.quantity));

  let next;

  if (qtyToMove < moveFor.quantity) {
    // ---- PARTIAL MOVE (split stack) ----
    next = rows
      .map(r =>
        r.id === moveFor.id
          ? { ...r, quantity: r.quantity - qtyToMove }
          : r
      )
      .concat({
        ...moveFor,
        id: genId(),
        location: destination,
        quantity: qtyToMove,
      });

    await cmd.assets.historyAppend({
      date: new Date().toISOString().slice(0,10),
      id: moveFor.id,
      startPosition: prev,
      endPosition: destination
    });
  } else {
    // ---- FULL MOVE (existing behaviour) ----
    next = rows.map(r =>
      r.id === moveFor.id ? { ...r, location: destination } : r
    );

    await cmd.assets.historyAppend({
      date: new Date().toISOString().slice(0,10),
      id: moveFor.id,
      startPosition: prev,
      endPosition: destination
    });
  }

  try {
    await cmd.assets.replace(next);
    setRows(next);
  } catch (e) {
    setError(e?.response?.data?.error || e?.message || 'Failed to move asset');
  } finally {
    setMoveOpen(false);
    setMoveFor(null);
    setDest('');
    setMoveQty(1);
  }
}

  async function saveAsset(e) {
  e.preventDefault();

  if (!newAsset.item?.trim()) {
    setError('Item name is required');
    return;
  }

  const record = {
    id: genId(),
    item: newAsset.item.trim(),
    make: newAsset.make?.trim() || '',
    model: newAsset.model?.trim() || '',
    serial: newAsset.serial?.trim() || '',
    supplier: newAsset.supplier?.trim() || '',
    location: newAsset.location?.trim() || '—',
    purchaseDate: newAsset.purchaseDate || '',
    purchasePrice: Number(newAsset.purchasePrice || 0),
    category: newAsset.category?.trim() || '',
    status: newAsset.status?.trim() || '—',
    quantity: Math.max(1, Number(newAsset.quantity || 1)),
  };

  try {
    let next;

    if (editing) {
      // ---- UPDATE ----
      next = rows.map(r =>
        r.id === editing.id ? { ...r, ...record, id: editing.id } : r
      );
    } else {
      // ---- ADD (FULL LIST REPLACE) ----
      next = [...rows, record];
    }

    // 🔑 THIS IS THE IMPORTANT PART
    await cmd.assets.replace(next); // POST /assets

    setRows(next);
    setAdding(false);
    setEditing(null);
    setNewAsset({ ...EMPTY_ASSET });
  } catch (e) {
    setError(
      e?.response?.data?.error ||
      e?.message ||
      'Failed to save asset'
    );
  }
}

  async function confirmDelete(asset) {
  if (!asset) return;

  const ok = window.confirm(
    `Are you sure you want to permanently delete:\n\n` +
    `${asset.item}${asset.model ? ` (${asset.model})` : ''}?`
  );
  if (!ok) return;

  try {
    // Preferred: dedicated delete endpoint
    await cmd.assets.delete(asset.id);

    // Optimistic UI update
    setRows(prev => prev.filter(r => r.id !== asset.id));
  } catch (e) {
    setError(
      e?.response?.data?.error ||
      e?.message ||
      'Failed to delete asset'
    );
  }
}

useEffect(() => {
  function onClick(e) {
    if (!e.target.closest('.actions-menu')) {
      setOpenActionsFor(null);
    }
  }
  document.addEventListener('click', onClick);
  return () => document.removeEventListener('click', onClick);
}, []);

function openEdit(asset) {
  setEditing(asset);
  setAdding(true); // reuse the same form
  setNewAsset({
    item: asset.item || '',
    make: asset.make || '',
    model: asset.model || '',
    serial: asset.serial || '',
    supplier: asset.supplier || '',
    location: asset.location || '',
    purchaseDate: asset.purchaseDate || '',
    purchasePrice: asset.purchasePrice || '',
    category: asset.category || '',
    status: asset.status || '',
    quantity: asset.quantity || 1,
  });
}

function resetForm() {
  setNewAsset({ ...EMPTY_ASSET });
  setEditingId(null);
}

  return (
    <div className="container">
      <PageHeader
        eyebrow="Assets"
        title="Assets"
        actions={
          <>
            <button
              className="btn secondary"
              onClick={()=>setGroupMode(m => !m)}
              title="Toggle grouped view"
            >
              {groupMode ? 'Detail view' : 'Group view'}
            </button>
            <button
              className={adding ? 'btn secondary' : 'btn'}
              onClick={() => {
                resetForm();
                setAdding(v => !v);
              }}
            >
              {adding ? 'Close' : <><Icon name="plus" size={15} /> Add asset</>}
            </button>
          </>
        }
      />

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      {/* Add form */}
      {adding && (
        <div className="card mb-16">
          <div className="card-head">
            <h2 className="card-title">{editing ? 'Edit asset' : 'Add asset'}</h2>
          </div>
          <form className="add-form assets" onSubmit={saveAsset}>
            <div className="field">
              <label className="input-label">Item <span className="req">*</span></label>
              <input className="input" value={newAsset.item} onChange={e=>setNewAsset(v=>({...v, item:e.target.value}))} placeholder="e.g. Laptop" autoFocus />
            </div>
            <div className="field"><label className="input-label">Make</label>
              <input className="input" value={newAsset.make} onChange={e=>setNewAsset(v=>({...v, make:e.target.value}))} placeholder="e.g. Apple" />
            </div>
            <div className="field"><label className="input-label">Model</label>
              <input className="input" value={newAsset.model} onChange={e=>setNewAsset(v=>({...v, model:e.target.value}))} placeholder="e.g. 16 Pro" />
            </div>
            <div className="field"><label className="input-label">Serial</label>
              <input className="input" value={newAsset.serial} onChange={e=>setNewAsset(v=>({...v, serial:e.target.value}))} placeholder="e.g. SN12345" />
            </div>
            <div className="field"><label className="input-label">Supplier</label>
              <input className="input" value={newAsset.supplier} onChange={e=>setNewAsset(v=>({...v, supplier:e.target.value}))} placeholder="e.g. Ernest Doe & Sons" />
            </div>

            {/* Free-text with suggestions */}
            <ComboInput
              id="asset-location"
              label="Location"
              value={newAsset.location}
              onChange={v => setNewAsset(a => ({ ...a, location: v }))}
              options={locationSuggestions}
              placeholder="Type or pick a location"
            />

            <div className="field small"><label className="input-label">Purchase date</label>
              <input className="input" type="date" value={newAsset.purchaseDate || ''} onChange={e=>setNewAsset(v=>({...v, purchaseDate:e.target.value}))} />
            </div>
            <div className="field small"><label className="input-label">Purchase price</label>
              <div className="input-wrap"><span className="prefix">£</span>
                <input className="input" type="number" step="0.01" value={newAsset.purchasePrice} onChange={e=>setNewAsset(v=>({...v, purchasePrice:e.target.value}))} placeholder="0.00" />
              </div>
            </div>
            <div className="field"><label className="input-label">Category</label>
              <input className="input" value={newAsset.category} onChange={e=>setNewAsset(v=>({...v, category:e.target.value}))} placeholder="e.g. Mobile" />
            </div>
            <div className="field"><label className="input-label">Status</label>
              <input className="input" value={newAsset.status} onChange={e=>setNewAsset(v=>({...v, status:e.target.value}))} placeholder="e.g. In service" />
            </div>
            <div className="field small"><label className="input-label">Qty</label>
              <input className="input" type="number" min="1" step="1" value={newAsset.quantity} onChange={e=>setNewAsset(v=>({...v, quantity:e.target.value}))} />
            </div>

            <div className="meta">
              <div className="preview" />
              <div className="actions">
                <button className="btn secondary" type="button" onClick={()=>{setAdding(false); resetForm()}}>Close</button>
                <button className="btn" type="submit" disabled={!newAsset.item?.trim()}>Save asset</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Table(s) */}
      <div className="card">
        <div className="toolbar mb-16">
          <input
            className="input grow"
            placeholder="Search assets"
            value={q}
            onChange={e=>setQ(e.target.value)}
          />
          <select className="input" value={supplier} onChange={e=>setSupplier(e.target.value)}>
            {suppliers.map(s => <option key={s} value={s}>{s==='all' ? 'All suppliers' : s}</option>)}
          </select>
          <select className="input" value={location} onChange={e=>setLocation(e.target.value)}>
            {locations.map(l => <option key={l} value={l}>{l==='all' ? 'All locations' : l}</option>)}
          </select>
          {(q || supplier !== 'all' || location !== 'all') && (
            <button className="btn ghost" onClick={()=>{ setQ(''); setSupplier('all'); setLocation('all'); }}>Clear filters</button>
          )}
        </div>

        {groupMode && (
          <div className="toolbar mb-16">
            <span className="muted">Group by</span>
            <select className="input" value={groupA} onChange={e=>setGroupA(e.target.value)}>
              {GROUP_FIELDS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            <span className="muted">then</span>
            <select className="input" value={groupB} onChange={e=>setGroupB(e.target.value)}>
              {GROUP_FIELDS.filter(g => g.key !== groupA).map(g => (
                <option key={g.key} value={g.key}>{g.label}</option>
              ))}
            </select>
          </div>
        )}

        {loading ? <div className="empty">Loading</div> : (
          <>
            {!groupMode ? (
              // ---------- DETAIL MODE ----------
              filtered.length === 0 ? (
                <div className="empty">No assets found.</div>
              ) : (
                <>
                  <div className="table-wrap" style={{ overflowY:'visible', paddingBottom: openActionsFor ? 120 : 0 }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Model</th>
                          <th>Serial</th>
                          <th>Supplier</th>
                          <th>Location</th>
                          <th>Purchased</th>
                          <th className="num">Price</th>
                          <th>Category</th>
                          <th className="num">Qty</th>
                          <th className="actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((a) => (
                          <tr key={a.id}>
                            <td className="cell-strong">{a.item || '—'}</td>
                            <td>{[a.make, a.model].filter(Boolean).join(' ') || '—'}</td>
                            <td className="cell-muted">{a.serial || '—'}</td>
                            <td className="cell-muted">{a.supplier || '—'}</td>
                            <td>{a.location || '—'}</td>
                            <td className="nowrap">{fmtDay(a.purchaseDate)}</td>
                            <td className="num">{fmtGBP(a.purchasePrice)}</td>
                            <td>{a.category || '—'}</td>
                            <td className="num">{fmtInt(a.quantity)}</td>
                            <td className="actions-td">
                              <div className="actions-menu">
                                <button
                                  className="btn secondary small"
                                  aria-haspopup="menu"
                                  aria-expanded={openActionsFor === a.id}
                                  onClick={() =>
                                    setOpenActionsFor(openActionsFor === a.id ? null : a.id)
                                  }
                                >
                                  Actions <Icon name="chevronDown" size={14} />
                                </button>

                                {openActionsFor === a.id && (
                                  <div className="actions-dropdown" role="menu">
                                    <button role="menuitem" onClick={() => {
                                      setOpenActionsFor(null);
                                      openEdit(a);
                                    }}>
                                      Edit
                                    </button>

                                    <button role="menuitem" onClick={() => {
                                      setOpenActionsFor(null);
                                      openMove(a);
                                    }}>
                                      Move
                                    </button>

                                    <button
                                      role="menuitem"
                                      className="danger"
                                      onClick={() => {
                                        setOpenActionsFor(null);
                                        confirmDelete(a);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="pager">
                    <span className="pager-info">Page {page} of {totalPages}, showing {pageRows.length} of {filtered.length}</span>
                    <div className="pager-buttons">
                      <button className="btn secondary" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1, p-1))}>Previous</button>
                      <button className="btn secondary" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages, p+1))}>Next</button>
                    </div>
                  </div>
                </>
              )
            ) : (
              // ---------- GROUP MODE ----------
              (grouped.rows?.length ?? 0) === 0 ? (
                <div className="empty">No groups to show.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{GROUP_FIELDS.find(g => g.key === groupA)?.label || 'Group A'}</th>
                        <th>{GROUP_FIELDS.find(g => g.key === groupB)?.label || 'Group B'}</th>
                        <th className="num">Total qty</th>
                        <th className="num">Total value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.rows.map((g, idx) => (
                        <tr key={`${g.a}::${g.b}::${idx}`}>
                          <td className="cell-strong nowrap">{g.a}</td>
                          <td className="nowrap">{g.b}</td>
                          <td className="num">{fmtInt(g.qty)}</td>
                          <td className="num">{fmtGBP(g.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{textAlign:'right'}}>Totals</td>
                        <td className="num">{fmtInt(grouped.grandQty)}</td>
                        <td className="num">{fmtGBP(grouped.grandValue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Move dialog */}
      {moveOpen && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true">
            <div className="modal-head">
              <h3>Move asset</h3>
              <p className="modal-sub">
                <strong>{moveFor?.item}</strong>
                {moveFor?.model ? ` ${moveFor.model}` : ''}, currently at {moveFor?.location || 'no location'}
              </p>
            </div>

            <div className="form-grid cols-2">
              {/* Quantity selector (only if stack > 1) */}
              {Number(moveFor?.quantity) > 1 && (
                <label className="input-label">
                  Quantity to move
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={moveFor.quantity}
                    value={moveQty}
                    onChange={e =>
                      setMoveQty(
                        Math.max(1, Math.min(moveFor.quantity, Number(e.target.value || 1)))
                      )
                    }
                  />
                  <div className="hint">{fmtInt(moveFor.quantity)} available</div>
                </label>
              )}

              <div className={Number(moveFor?.quantity) > 1 ? '' : 'span-all'}>
                <ComboInput
                  id="dest-location"
                  label={teamsLoading ? 'Destination (loading teams)' : 'Destination'}
                  value={dest}
                  onChange={setDest}
                  options={locationSuggestions}
                  placeholder="Type a team or new location"
                />
              </div>
            </div>

            <div className="modal-foot">
              <button
                className="btn secondary"
                onClick={()=>{
                  setMoveOpen(false);
                  setMoveFor(null);
                  setDest('');
                  setMoveQty(1);
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                disabled={!dest?.trim()}
                onClick={confirmMove}
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
